import { useEffect, useState } from "react";
import { CheckCircle2, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading } from "../../components/shared";

export default function ResidentVisitorApprovals() {
  const [visits, setVisits] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({
    unit_id: "",
    visitor_name: "",
    visitor_type: "Guest",
    purpose: "",
    vehicle_number: "",
    valid_from: "",
    valid_until: "",
    multiple_entry: false,
  });
  const load = () =>
    Promise.all([
      api.get("/resident-gate/approvals"),
      api.get("/resident-gate/invitations"),
      api.get("/resident-gate/context"),
    ])
      .then(([approvals, invites, context]) => {
        setVisits(approvals.data);
        setInvitations(invites.data);
        setUnits(context.data.units);
      })
      .catch(() => setVisits([]));
  useEffect(() => {
    load();
  }, []);
  const decide = async (visitId, decision) => {
    try {
      await api.post(`/resident-gate/visits/${visitId}/decision`, { decision });
      toast.success(`Visitor ${decision.toLowerCase()}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record decision");
    }
  };
  const invite = async () => {
    if (!form.valid_from || !form.valid_until) {
      toast.error("Select a valid-from and valid-until time");
      return;
    }
    if (new Date(form.valid_until) <= new Date(form.valid_from)) {
      toast.error("Valid until must be after valid from");
      return;
    }
    try {
      await api.post("/resident-gate/invitations", form);
      toast.success("Expected visitor created");
      setForm({
        unit_id: "",
        visitor_name: "",
        visitor_type: "Guest",
        purpose: "",
        vehicle_number: "",
        valid_from: "",
        valid_until: "",
        multiple_entry: false,
      });
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create invitation",
      );
    }
  };
  const cancel = async (id) => {
    try {
      await api.post(`/resident-gate/invitations/${id}/cancel`);
      toast.success("Invitation cancelled");
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not cancel invitation",
      );
    }
  };
  if (!visits) return <Loading />;
  return (
    <div data-testid="resident-visitor-approvals">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Gate security
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold">
          Visitor Approvals
        </h1>
      </header>
      <section
        className="mt-6 border border-slate-200 bg-white p-5"
        data-testid="resident-invitation-form"
      >
        <h2 className="font-display text-xl font-semibold">Expected visitor</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="visitor-invitation-unit"
            onChange={(event) =>
              setForm({ ...form, unit_id: event.target.value })
            }
            value={form.unit_id}
          >
            <option value="">Select authorized unit</option>
            {units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_number}
              </option>
            ))}
          </select>
          <input
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="visitor-invitation-name"
            onChange={(event) =>
              setForm({ ...form, visitor_name: event.target.value })
            }
            placeholder="Visitor name"
            value={form.visitor_name}
          />
          <select
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="visitor-invitation-type"
            onChange={(event) =>
              setForm({ ...form, visitor_type: event.target.value })
            }
            value={form.visitor_type}
          >
            {["Guest", "Delivery", "Service / Technician", "Cab / Driver", "Other"].map(
              (t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ),
            )}
          </select>
          <input
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="visitor-invitation-purpose"
            onChange={(event) =>
              setForm({ ...form, purpose: event.target.value })
            }
            placeholder="Purpose (optional)"
            value={form.purpose}
          />
          <input
            className="rounded-md border border-slate-300 px-3 py-2"
            data-testid="visitor-invitation-vehicle"
            onChange={(event) =>
              setForm({ ...form, vehicle_number: event.target.value })
            }
            placeholder="Vehicle number (optional)"
            value={form.vehicle_number}
          />
          <label className="text-xs text-slate-500">
            Valid from
            <input
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              data-testid="visitor-invitation-from"
              onChange={(event) =>
                setForm({ ...form, valid_from: event.target.value })
              }
              type="datetime-local"
              value={form.valid_from}
            />
          </label>
          <label className="text-xs text-slate-500">
            Valid until
            <input
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              data-testid="visitor-invitation-until"
              onChange={(event) =>
                setForm({ ...form, valid_until: event.target.value })
              }
              type="datetime-local"
              value={form.valid_until}
            />
          </label>
          <label
            className="flex items-center gap-2 text-sm text-slate-600"
            data-testid="visitor-invitation-multiple"
          >
            <input
              checked={form.multiple_entry}
              onChange={(event) =>
                setForm({ ...form, multiple_entry: event.target.checked })
              }
              type="checkbox"
            />
            Allow multiple entries within validity
          </label>
        </div>
        <Button
          className="mt-4"
          data-testid="visitor-invitation-create"
          onClick={invite}
          size="sm"
        >
          <Plus className="h-4 w-4" />
          Create invitation
        </Button>
        <div className="mt-4 space-y-2" data-testid="resident-invitation-list">
          {invitations.map((item) => (
            <div
              className="border border-slate-200 p-3 text-sm"
              key={item.invitation_id}
            >
              {item.visitor_name} · Unit {item.unit_number} ·{" "}
              {item.computed_status || item.status}
              {item.status === "ACTIVE" && !item.active_visit_id && (
                <Button
                  className="ml-3"
                  data-testid={`visitor-invitation-cancel-${item.invitation_id}`}
                  onClick={() => cancel(item.invitation_id)}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>
      <section className="mt-6 space-y-3">
        {visits.length ? (
          visits.map((visit) => (
            <article
              className="border border-slate-200 bg-white p-5"
              key={visit.visit_id}
            >
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-semibold">
                    {visit.visitor_name}
                  </h2>
                  <p className="mt-2 text-sm text-slate-600">
                    {visit.visitor_type} · Unit {visit.unit_number} ·{" "}
                    {visit.arrival_gate_name}
                  </p>
                  {visit.purpose && (
                    <p className="mt-1 text-sm text-slate-600">
                      {visit.purpose}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`visitor-approve-${visit.visit_id}`}
                    onClick={() => decide(visit.visit_id, "APPROVED")}
                    size="sm"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    data-testid={`visitor-reject-${visit.visit_id}`}
                    onClick={() => decide(visit.visit_id, "REJECTED")}
                    size="sm"
                    variant="outline"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                </div>
              </div>
            </article>
          ))
        ) : (
          <p
            className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600"
            data-testid="visitor-approvals-empty"
          >
            No visitor approvals are waiting for your authorized units.
          </p>
        )}
      </section>
    </div>
  );
}
